import { datadogRum } from '@datadog/browser-rum';

const BRIDGE_REQUEST_TYPE = 'DD_RUM_PARENT_CONTEXT_REQUEST' as const;
const BRIDGE_RESPONSE_TYPE = 'DD_RUM_PARENT_CONTEXT_RESPONSE' as const;

interface ParentContextResponse {
  type: typeof BRIDGE_RESPONSE_TYPE;
  sessionId: string;
}

function isParentContextResponse(data: unknown): data is ParentContextResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as ParentContextResponse).type === BRIDGE_RESPONSE_TYPE &&
    typeof (data as ParentContextResponse).sessionId === 'string'
  );
}

export function isRunningInIframe(): boolean {
  try {
    return globalThis.self !== globalThis.top;
  } catch {
    // Cross-origin access blocked — we are definitely in an iframe
    return true;
  }
}

/**
 * Requests the parent window's Datadog RUM session ID via postMessage and
 * sets it as `parent_session_id` in the iframe's global RUM context.
 *
 * This links the iframe's RUM session to the parent's in Datadog, making it
 * possible to trace user journeys across both applications.
 *
 * Must be called AFTER `datadogRum.init()`.
 */
export function initDatadogIframeBridge(): void {
  if (!isRunningInIframe()) return;

  let parentOrigin: string;
  try {
    parentOrigin = new URL(document.referrer).origin;
  } catch {
    return;
  }

  const timeoutId = globalThis.setTimeout(() => {
    globalThis.removeEventListener('message', handleMessage);
  }, 5000);

  const handleMessage = (event: MessageEvent): void => {
    if (event.source !== globalThis.parent) return;
    if (event.origin !== parentOrigin) return;
    if (!isParentContextResponse(event.data)) return;

    const { sessionId } = event.data;

    if (sessionId) {
      datadogRum.setGlobalContextProperty('parent_session_id', sessionId);
    }

    globalThis.clearTimeout(timeoutId);
    globalThis.removeEventListener('message', handleMessage);
  };

  globalThis.addEventListener('message', handleMessage);

  globalThis.parent.postMessage({ type: BRIDGE_REQUEST_TYPE }, parentOrigin);
}
